import React from "react";
import { motion } from "framer-motion";
import { blogs, blogsHeader } from "../portfolio";
import { blurIn, staggerContainer, fadeIn } from "../utils/motion";
import { FiArrowUpRight, FiBookOpen } from "react-icons/fi";
import { FaDev } from "react-icons/fa";
import SpotlightCard from "./SpotlightCard";
import ScrambleText from "./ScrambleText";
import { playClick, playHover } from "../utils/soundEngine";

function BlogCard({ blog, index }) {
  return (
    <motion.div variants={fadeIn("up", index * 0.12)} className="h-full">
      <a
        href={blog.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => playClick()}
        onMouseEnter={() => playHover()}
        className="block h-full"
      >
        <SpotlightCard
          className="group relative flex flex-col justify-between h-full p-6 sm:p-7 rounded-2xl border border-surface/[0.08] bg-surface/[0.015] hover:bg-surface/[0.035] hover:border-surface/[0.16] transition-all duration-400 ease-out-expo shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
          spotlightColor="rgba(var(--color-surface), 0.05)"
        >
          {/* Subtle Ambient Hover Glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-surface/[0.03] rounded-full blur-2xl pointer-events-none group-hover:bg-surface/[0.06] transition-colors duration-500" />

          <div>
            {/* Metadata Row */}
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2 text-xs font-mono text-fg-faint">
                <span className="flex items-center gap-1">
                  <FiBookOpen size={12} className="opacity-60" />
                  {blog.readTime}
                </span>
                <span>•</span>
                <span>{blog.date}</span>
              </div>

              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface/[0.04] border border-surface/[0.06] text-[10px] font-mono text-fg-faint">
                <FaDev size={11} className="opacity-75" />
                {blog.platform}
              </span>
            </div>

            {/* Article Title */}
            <h3 className="font-display text-lg sm:text-xl font-bold text-fg mb-3 leading-snug group-hover:text-fg transition-colors duration-300">
              {blog.title}
            </h3>

            {/* Description */}
            <p className="text-xs sm:text-sm text-fg-muted leading-relaxed mb-6">
              {blog.description}
            </p>
          </div>

          {/* Footer */}
          <div className="pt-4 border-t border-surface/[0.06] flex items-center justify-between gap-3 flex-wrap">
            {/* Tags */}
            <div className="flex flex-wrap gap-1.5">
              {blog.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded bg-surface/[0.03] border border-surface/[0.04] text-[10.5px] font-mono text-fg-faint"
                >
                  #{tag.toLowerCase()}
                </span>
              ))}
            </div>

            {/* CTA Link */}
            <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-fg group-hover:text-fg-muted transition-colors duration-300 shrink-0">
              Read Article
              <FiArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          </div>
        </SpotlightCard>
      </a>
    </motion.div>
  );
}

function Blogs() {
  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.05 }}
    >
      <motion.div variants={blurIn("up")} className="mb-12">
        <span className="section-label">Articles</span>
        <h2 className="section-title">
          <ScrambleText text={blogsHeader.title} hover={true} />
        </h2>
        <p className="section-subtitle">{blogsHeader.description}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {blogs.data.map((blog, i) => (
          <BlogCard key={blog.title} blog={blog} index={i} />
        ))}
      </div>
    </motion.div>
  );
}

export default Blogs;
